import { zodResponsesFunction, zodTextFormat } from 'openai/helpers/zod';
import type { AgentOutputType } from '../agent';
import { UserError } from '../errors';
import type { ToolInputParameters } from '../tool';
import type {
  JsonObjectSchema,
  JsonSchemaDefinition,
  TextOutput,
} from '../types';
import {
  stripStrictNullsForJsonSchema,
  stripStrictNullsForZodSchema,
  toOpenAIStrictToolSchema,
} from './strict-tool-schema';
import { isZodObject } from './type-guards';
import { asZodType, type ZodObjectLike } from './zod-compact';
import {
  hasJsonSchemaObjectShape,
  mergeJsonSchemaDescriptions,
  zodJsonSchemaCompat,
} from './zod-json-schema-compat';

type MinimalParseableTextFormat = {
  type: 'json_schema';
  name: string;
  strict?: boolean;
  schema: unknown;
};

// TypeScript struggles to infer the heavily generic types returned by the OpenAI
// helpers, so we provide minimal wrappers that sidestep the deep instantiation.
type MinimalParseableResponseTool = {
  parameters: unknown;
  $parseRaw: (input: string) => unknown;
};

type ZodResponsesFunctionOptions = {
  name: string;
  parameters: unknown;
  function?: (...args: any[]) => unknown;
  description?: string;
};

const zodResponsesFunctionCompat: (
  options: ZodResponsesFunctionOptions,
) => MinimalParseableResponseTool = zodResponsesFunction as unknown as (
  options: ZodResponsesFunctionOptions,
) => MinimalParseableResponseTool;

export type FunctionToolName = string & { __brand?: 'ToolName' } & {
  readonly __pattern?: '^[a-zA-Z0-9_]+$';
};

export function toFunctionToolName(name: string): FunctionToolName {
  // Replace spaces with underscores
  name = name.replace(/\s/g, '_');

  // Replace non-alphanumeric characters with underscores
  name = name.replace(/[^a-zA-Z0-9]/g, '_');

  // Ensure the name is not empty
  if (name.length === 0) {
    throw new Error('Tool name cannot be empty');
  }

  return name as FunctionToolName;
}

/**
 * Get the schema and parser from an input type. If the input type is a ZodObject, we will convert
 * it into a JSON Schema and use Zod as parser. If the input type is a JSON schema, we use the
 * JSON.parse function to get the parser.
 * @param inputType - The input type to get the schema and parser from.
 * @param name - The name of the tool.
 * @returns The schema and parser.
 */
export function getSchemaAndParserFromInputType<T extends ToolInputParameters>(
  inputType: T,
  name: string,
  options: {
    strict?: boolean;
  } = {},
): {
  schema: JsonObjectSchema<any>;
  parser: (input: string) => any;
} {
  const parser = (input: string) => JSON.parse(input);

  if (isZodObject(inputType)) {
    const useFallback = (originalError?: unknown) => {
      const fallbackSchema = buildJsonSchemaFromZod(inputType);
      if (fallbackSchema) {
        return {
          schema: options.strict
            ? toOpenAIStrictToolSchema(fallbackSchema)
            : fallbackSchema,
          parser: (rawInput: string) =>
            inputType.parse(
              options.strict
                ? stripStrictNullsForZodSchema(inputType, JSON.parse(rawInput))
                : JSON.parse(rawInput),
            ),
        };
      }

      const errorMessage =
        originalError instanceof Error
          ? ` Upstream helper error: ${originalError.message}`
          : '';

      throw new UserError(
        `Unable to convert the provided Zod schema to JSON Schema. Ensure that the \`zod\` package is available at runtime or provide a JSON schema object instead.${errorMessage}`,
      );
    };

    if (options.strict) {
      const fallbackSchema = buildJsonSchemaFromZod(inputType);
      if (fallbackSchema) {
        return {
          schema: toOpenAIStrictToolSchema(fallbackSchema),
          parser: (rawInput: string) =>
            inputType.parse(
              stripStrictNullsForZodSchema(inputType, JSON.parse(rawInput)),
            ),
        };
      }
    }

    let formattedFunction: MinimalParseableResponseTool;
    try {
      formattedFunction = zodResponsesFunctionCompat({
        name,
        parameters: asZodType(inputType),
        function: () => {}, // empty function here to satisfy the OpenAI helper
        description: '',
      });
    } catch (error) {
      return useFallback(error);
    }

    if (hasJsonSchemaObjectShape(formattedFunction.parameters)) {
      const fallbackSchema = buildJsonSchemaFromZod(inputType);
      if (fallbackSchema) {
        mergeJsonSchemaDescriptions(
          formattedFunction.parameters as JsonObjectSchema<any>,
          fallbackSchema,
        );
      }
      return {
        schema: options.strict
          ? toOpenAIStrictToolSchema(
              formattedFunction.parameters as JsonObjectSchema<any>,
            )
          : (formattedFunction.parameters as JsonObjectSchema<any>),
        parser: options.strict
          ? (rawInput: string) =>
              inputType.parse(
                stripStrictNullsForZodSchema(inputType, JSON.parse(rawInput)),
              )
          : formattedFunction.$parseRaw,
      };
    }

    return useFallback();
  } else if (typeof inputType === 'object' && inputType !== null) {
    return {
      schema: options.strict ? toOpenAIStrictToolSchema(inputType) : inputType,
      parser: options.strict
        ? (rawInput: string) =>
            stripStrictNullsForJsonSchema(inputType, JSON.parse(rawInput))
        : parser,
    };
  }

  throw new UserError('Input type is not a ZodObject or a valid JSON schema');
}

// openai/helpers/zod cannot emit strict schemas for every Zod runtime
// (notably Zod v4), so we delegate to a small local converter living in
// zodJsonSchemaCompat.ts whenever its output is missing the required JSON Schema bits.
function buildJsonSchemaFromZod(
  inputType: ZodObjectLike,
): JsonObjectSchema<any> | undefined {
  return zodJsonSchemaCompat(inputType);
}

// The `.schema` payload is all we need, so a lightweight signature keeps the compiler happy.
const zodTextFormatCompat: (
  zodObject: unknown,
  name: string,
  props?: unknown,
) => MinimalParseableTextFormat = zodTextFormat as unknown as (
  zodObject: unknown,
  name: string,
  props?: unknown,
) => MinimalParseableTextFormat;

/**
 * Converts the agent output type provided to a serializable version
 */
export function convertAgentOutputTypeToSerializable(
  outputType: AgentOutputType,
): JsonSchemaDefinition | TextOutput {
  if (outputType === 'text') {
    return 'text';
  }

  if (isZodObject(outputType)) {
    const fallbackSchema = buildJsonSchemaFromZod(outputType);
    if (fallbackSchema && hasOptionalJsonSchemaProperties(fallbackSchema)) {
      return {
        type: 'json_schema',
        name: 'output',
        strict: false,
        schema: fallbackSchema,
      };
    }

    const useFallback = (
      existing?: MinimalParseableTextFormat,
      originalError?: unknown,
    ): JsonSchemaDefinition => {
      if (fallbackSchema) {
        return {
          type: existing?.type ?? 'json_schema',
          name: existing?.name ?? 'output',
          strict: existing?.strict ?? false,
          schema: fallbackSchema,
        };
      }

      const errorMessage =
        originalError instanceof Error
          ? ` Upstream helper error: ${originalError.message}`
          : '';

      throw new UserError(
        `Unable to convert the provided Zod schema to JSON Schema. Ensure that the \`zod\` package is available at runtime or provide a JSON schema object instead.${errorMessage}`,
      );
    };

    let output: MinimalParseableTextFormat;
    try {
      output = zodTextFormatCompat(asZodType(outputType), 'output');
    } catch (error) {
      return useFallback(undefined, error);
    }

    if (hasJsonSchemaObjectShape(output.schema)) {
      return {
        type: output.type,
        name: output.name,
        strict: output.strict || false,
        schema: output.schema as JsonObjectSchema<any>,
      };
    }

    return useFallback(output);
  }

  return outputType;
}

function hasOptionalJsonSchemaProperties(schema: unknown): boolean {
  if (typeof schema !== 'object' || schema === null) {
    return false;
  }

  const record = schema as Record<string, unknown>;
  if (
    record.type === 'object' &&
    typeof record.properties === 'object' &&
    record.properties !== null &&
    !Array.isArray(record.properties)
  ) {
    const properties = record.properties as Record<string, unknown>;
    const required = new Set(
      Array.isArray(record.required) ? record.required.map(String) : [],
    );
    if (Object.keys(properties).some((key) => !required.has(key))) {
      return true;
    }
    if (
      Object.values(properties).some((property) =>
        hasOptionalJsonSchemaProperties(property),
      )
    ) {
      return true;
    }
  }

  if (hasOptionalJsonSchemaProperties(record.additionalProperties)) {
    return true;
  }

  const items = record.items;
  if (Array.isArray(items)) {
    return items.some((item) => hasOptionalJsonSchemaProperties(item));
  }
  if (hasOptionalJsonSchemaProperties(items)) {
    return true;
  }

  for (const key of ['anyOf', 'allOf', 'oneOf']) {
    const entries = record[key];
    if (
      Array.isArray(entries) &&
      entries.some((entry) => hasOptionalJsonSchemaProperties(entry))
    ) {
      return true;
    }
  }

  return false;
}
